import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AuthenticationStore from '~/auth/authenticationStore';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import {
  redirectToClientHome,
  redirectToDomainLogin,
} from '~/services/redirects';

import { AppCrashFeedbackModal } from './AppCrashFeedbackModal';

const messages = defineMessages({
  somethingWentWrong: {
    id: 'ErrorBoundary.somethingWentWrong',
    defaultMessage: 'Sorry! Something went wrong.',
    description: 'Error boundary main error message',
  },
  encounteredIssue: {
    id: 'ErrorBoundary.encounteredIssue',
    defaultMessage:
      "Looks like we've encountered an issue. It has been reported to our team and we will get around to fixing it soon. Sorry for the inconvenience.",
    description: 'Error boundary explanation message',
  },
  goToHome: {
    id: 'ErrorBoundary.goToHome',
    defaultMessage: 'Go to Home',
    description: 'Button to go to home page from error boundary',
  },
  logo: {
    id: 'ErrorBoundary.logo',
    defaultMessage: 'Evisort',
    description: 'Alternative text for the logo on the error boundary',
  },
  shareDetails: {
    id: 'ErrorBoundary.shareDetails',
    defaultMessage: 'Share What Happened',
    description:
      'Button that opens a form for reporting details about the error',
  },
  detailsSent: {
    id: 'ErrorBoundary.detailsSent',
    defaultMessage: 'Details sent. Thank you.',
    description: 'Confirmation shown after the error details are reported',
  },
});

interface ErrorBoundaryProps {
  /** Sentry id of the caught error, used to attach the report to it. */
  eventId?: string;
}

function ErrorBoundary({ eventId }: ErrorBoundaryProps) {
  const intl = useIntl();
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);

  const handleGoHome = () => {
    if (AuthenticationStore().isLoggedIn()) {
      redirectToClientHome();
    } else {
      redirectToDomainLogin();
    }
  };

  return (
    <Layout
      align="center"
      direction="column"
      p={6}
      styles={{ textAlign: 'center' }}
    >
      <Box>
        <Box mb={6}>
          <img
            src="/evisort.png"
            alt={intl.formatMessage(messages.logo)}
            style={{ width: '180px' }}
          />
        </Box>
        <Box py={6}>
          <Text variant="subtitle">
            {intl.formatMessage(messages.somethingWentWrong)}
          </Text>
        </Box>
        <Box py={4}>
          <Text color="text.secondary" variant="body">
            {intl.formatMessage(messages.encounteredIssue)}
          </Text>
        </Box>
        <Layout align="center" direction="column" p={6} spacing={4}>
          <Layout align="center" justify="center" spacing={3} wrap>
            <Button
              text={intl.formatMessage(messages.goToHome)}
              variant="secondary"
              onClick={handleGoHome}
            />
            {!isFeedbackSent && (
              <Button
                icon="message"
                iconPosition="left"
                level="ghost"
                text={intl.formatMessage(messages.shareDetails)}
                onClick={() => setIsFeedbackVisible(true)}
              />
            )}
          </Layout>
          {isFeedbackSent && (
            <Text as="p" color="text.secondary" variant="body">
              {intl.formatMessage(messages.detailsSent)}
            </Text>
          )}
        </Layout>
      </Box>
      <AppCrashFeedbackModal
        eventId={eventId}
        isVisible={isFeedbackVisible}
        onHide={() => setIsFeedbackVisible(false)}
        onSent={() => setIsFeedbackSent(true)}
      />
    </Layout>
  );
}

export default ErrorBoundary;
